if (!customElements.get('smk-recently-viewed-products')) {
  customElements.define('smk-recently-viewed-products', class extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._storageKey = 'suimok:recently-viewed-products:v1';
      this._currentIndex = 0;
      this._isMobile = window.innerWidth <= 1024;
      this._scheduleLoad();
    }

    disconnectedCallback() {
      if (this._idleTimer) window.clearTimeout(this._idleTimer);
      if (this._handleResize) window.removeEventListener('resize', this._handleResize);
      if (this._nextButton && this._handleNext) this._nextButton.removeEventListener('click', this._handleNext);
      if (this._track && this._touchStart) this._track.removeEventListener('touchstart', this._touchStart);
      if (this._track && this._touchEnd) this._track.removeEventListener('touchend', this._touchEnd);
    }

    _scheduleLoad() {
      const start = () => {
        this._idleTimer = window.setTimeout(() => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(() => this._load(), { timeout: 3000 });
          } else {
            this._load();
          }
        }, 1500);
      };

      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    }

    _readHistory() {
      try {
        const stored = JSON.parse(window.localStorage.getItem(this._storageKey) || '[]');
        if (!Array.isArray(stored)) return [];
        return stored.filter((handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(handle));
      } catch (error) {
        return [];
      }
    }

    _saveCurrentProduct(history) {
      const currentHandle = this.dataset.currentProduct;
      if (!currentHandle) return;
      const historyLimit = Number.parseInt(this.dataset.historyLimit, 10) || 12;
      const updatedHistory = [currentHandle, ...history.filter((handle) => handle !== currentHandle)].slice(0, historyLimit);
      try {
        window.localStorage.setItem(this._storageKey, JSON.stringify(updatedHistory));
      } catch (error) {
        // The section remains optional when browser storage is unavailable.
      }
    }

    async _load() {
      const history = this._readHistory();
      this._saveCurrentProduct(history);

      const currentHandle = this.dataset.currentProduct;
      const productLimit = Number.parseInt(this.dataset.productLimit, 10) || 12;
      const handles = history.filter((handle) => handle !== currentHandle).slice(0, productLimit);
      if (handles.length === 0) return;

      const cards = new Array(handles.length);
      let cursor = 0;
      const worker = async () => {
        while (cursor < handles.length) {
          const index = cursor;
          cursor += 1;
          cards[index] = await this._fetchCard(handles[index]);
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, handles.length) }, worker));
      if (!this.isConnected) return;

      this._track = this.querySelector('[data-recently-viewed-track]');
      const validCards = cards.filter(Boolean);
      if (!this._track || validCards.length === 0) return;

      const fragment = document.createDocumentFragment();
      validCards.forEach((card) => fragment.appendChild(card));
      this._track.replaceChildren(fragment);
      this.hidden = false;
      this._setupSlider();
    }

    async _fetchCard(handle) {
      const rootUrl = `${this.dataset.rootUrl || '/'}${(this.dataset.rootUrl || '/').endsWith('/') ? '' : '/'}`;
      const url = `${rootUrl}products/${encodeURIComponent(handle)}?section_id=smk-recently-viewed-product-card`;
      try {
        const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) return null;
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const card = documentFragment.querySelector('[data-recently-viewed-card]');
        return card ? document.importNode(card, true) : null;
      } catch (error) {
        return null;
      }
    }

    _setupSlider() {
      this._nextButton = this.querySelector('[data-recently-viewed-next]');
      this._currentElement = this.querySelector('[data-recently-viewed-current]');
      this._totalElement = this.querySelector('[data-recently-viewed-total]');
      this._navigation = this.querySelector('[data-recently-viewed-nav]');
      this._items = this.querySelectorAll('.smk-recently-viewed__item');
      if (!this._track || !this._nextButton || this._items.length === 0) return;

      this._handleNext = () => {
        this._currentIndex += 1;
        if (this._currentIndex >= this._totalSlides) this._currentIndex = 0;
        this._updateSlider();
      };
      this._handlePrevious = () => {
        this._currentIndex -= 1;
        if (this._currentIndex < 0) this._currentIndex = this._totalSlides - 1;
        this._updateSlider();
      };
      this._handleResize = () => {
        const isMobile = window.innerWidth <= 1024;
        if (isMobile !== this._isMobile) {
          this._isMobile = isMobile;
          this._calculateSlides();
        } else {
          this._updateSlider();
        }
      };
      this._touchStartX = 0;
      this._touchStart = (event) => { this._touchStartX = event.touches[0].clientX; };
      this._touchEnd = (event) => {
        const distance = this._touchStartX - event.changedTouches[0].clientX;
        if (distance > 40) this._handleNext();
        if (distance < -40) this._handlePrevious();
      };

      this._nextButton.addEventListener('click', this._handleNext);
      window.addEventListener('resize', this._handleResize);
      this._track.addEventListener('touchstart', this._touchStart, { passive: true });
      this._track.addEventListener('touchend', this._touchEnd);
      this._calculateSlides();
    }

    _calculateSlides() {
      const itemsPerSlide = this._isMobile ? 2 : 6;
      this._totalSlides = Math.ceil(this._items.length / itemsPerSlide);
      if (this._currentIndex >= this._totalSlides) this._currentIndex = Math.max(0, this._totalSlides - 1);
      if (this._navigation) this._navigation.style.display = this._totalSlides > 1 ? 'flex' : 'none';
      if (this._totalElement) this._totalElement.textContent = this._totalSlides;
      this._updateSlider();
    }

    _updateSlider() {
      if (!this._track) return;
      const wrapperWidth = this._track.parentElement.offsetWidth;
      const distance = this._totalSlides > 1 ? this._currentIndex * (wrapperWidth + 16) : 0;
      this._track.style.transform = `translateX(-${distance}px)`;
      if (this._currentElement) this._currentElement.textContent = this._currentIndex + 1;
    }
  });
}
